import { isMeetingHost } from "../funcs/data";
import {
  exly_connect_apis,
  http_methods,
  redirect_old_flow_header,
  redirect_old_flow_query_params,
} from "../constants/api";
import { backend_host, user_backend_host } from "../constants/hostsUrl";
import { getJwtToken } from "./auth";
import { apiCall } from "../../features/Common/modules/Apis/Apis.utils";
import {
  exly_source_header,
  scoot_origin_header,
} from "../../features/Common/modules/Apis/Apis.constants";
import { logError } from "../funcs/logError";

export const informMeetingJoined = async ({
  meetingAttendeeRole,
  userId,
  creatorUsername,
  subDomain,
  meetingNumber,
}) => {
  if (isMeetingHost(meetingAttendeeRole)) {
    try {
      const jwtToken = await getJwtToken({
        userId,
        creatorUsername,
        subDomain,
        meetingAttendeeRole,
      });

      apiCall({
        url: backend_host + exly_connect_apis.inform_meeting_joined,
        method: http_methods.post,
        headers: {
          "jwt-token": jwtToken,
          ...exly_source_header,
          ...scoot_origin_header,
        },
        body: { meetingNumber },
      });
    } catch {}
  }
};

/**
 * Checks whether the host should be redirected to the old Zoom/ISV flow.
 *
 * @param {Object} headers - Request headers containing JWT token and source/origin metadata.
 * @returns {Promise<boolean>} Returns `true` if redirect to old flow is required (`data.redirect_to_old_flow` or `data.data.redirect_to_old_flow` is boolean true), otherwise `false`.
 *
 * On API call failure, the error is logged and the function safely falls back to `false` (no redirect).
 */
export const checkRedirectOldFlow = async (headers) => {
  try {
    const redirectCheckResponse = await apiCall({
      url: `${user_backend_host}${exly_connect_apis.host_redirect_old_flow}?${redirect_old_flow_query_params.listing_uuid}=`,
      method: http_methods.get,
      headers,
    });

    const isRedirectOldFlow =
      redirectCheckResponse?.data?.redirect_to_old_flow === true ||
      redirectCheckResponse?.data?.data?.redirect_to_old_flow === true;

    return isRedirectOldFlow;
  } catch (error) {
    logError({
      error,
      when: "redirectCheckResponse - checking redirect old flow",
      occuredAt: "src/utils/apis/meeting.js",
    });
    return false;
  }
};

export const fetchMeetingDetails = async ({
  meetingAttendeeRole,
  jwtToken,
  meetingUuid,
  userEmail,
  firstName,
  lastName,
  shareStartUrl = false,
  slotUuid,
}) => {
  const isHost = isMeetingHost(meetingAttendeeRole);
  const baseHeaders = {
    "jwt-token": jwtToken,
    ...exly_source_header,
    ...scoot_origin_header,
  };

  // Check if host request should be redirected to old flow
  const isRedirectOldFlow = isHost
    ? await checkRedirectOldFlow(baseHeaders)
    : false;

  const headers = {
    ...baseHeaders,
    ...(isRedirectOldFlow
      ? { [redirect_old_flow_header.key]: redirect_old_flow_header.value }
      : {}),
  };

  const startMeetingApiResponse = await apiCall({
    url:
      backend_host +
      (isHost
        ? exly_connect_apis.fetch_meeting_details_for_host
        : exly_connect_apis.fetch_meeting_details_for_customer),
    method: http_methods.post,
    headers,
    body: {
      meeting_uuid: meetingUuid,
      user_email: userEmail,
      first_name: firstName,
      last_name: lastName,
      // shareStartUrl - This is given in the fetch meeting details api, so if true then the api will provide the
      // actual zoom server - app.zoom.us meeting url and we redirect the user to that
      ...(shareStartUrl ? { shareStartUrl: true } : {}),
      ...(slotUuid ? { slot_uuid: slotUuid } : {}),
    },
  });

  return startMeetingApiResponse;
};
